import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Review } from '../components/ReviewItem/ReviewItem';
import { useAuth } from '../context/AuthContext';

// Interface for reviews as they exist in the database
export interface ReviewFromDB {
  id: string;
  title: string;
  description?: string;
  project_id?: string;
  owner_user_id: string;
  created_at: string;
  last_updated_at?: string;
  keys?: number;
  status?: string; // New status field: 'pending' or 'completed'
}

// Interface for profile data from the Profiles table
interface Profile {
  id: string;
  user_id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  created_at: string;
}

/**
 * Custom hook for managing reviews
 * @param projectId - The ID of the project whose reviews to fetch
 * @returns Object containing reviews, loading state, error state, and review operations
 */
export function useReviews(projectId: string | undefined) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ownerProfiles, setOwnerProfiles] = useState<Record<string, Profile>>({});

  // Fetch owner profiles for a list of user IDs
  const fetchOwnerProfiles = async (ownerIds: string[]) => {
    // Filter out IDs we already have
    const idsToFetch = ownerIds.filter(id => !ownerProfiles[id]);
    
    if (idsToFetch.length === 0) return;
    
    try {
      const { data, error } = await supabase
        .from('Profiles')
        .select('id, user_id, first_name, last_name, email, created_at')
        .in('user_id', idsToFetch);
        
      if (error) {
        return;
      }
      
      if (data && data.length > 0) {
        const newProfiles: Record<string, Profile> = {};
        data.forEach(profile => {
          newProfiles[profile.user_id] = profile;
        });
        
        setOwnerProfiles(prev => ({
          ...prev,
          ...newProfiles
        }));
      }
    } catch (err) {
      // Handle error silently
    }
  };

  // Get formatted name from profile
  const getFormattedName = (profile: Profile | undefined): string => {
    if (!profile) return 'Unknown';
    
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    } else if (profile.first_name) {
      return profile.first_name;
    } else if (profile.last_name) {
      return profile.last_name;
    } else {
      return profile.email || 'Unknown';
    }
  };

  // Fetch reviews when the project ID changes
  useEffect(() => {
    const fetchReviews = async () => {
      if (!user || !projectId) {
        setReviews([]);
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('Reviews')
          .select('*')
          .eq('project_id', projectId)
          .order('last_updated_at', { ascending: false }); // Sort by last_updated_at in descending order
          
        if (error) {
          setError('Failed to load reviews. Please try again.');
          return;
        }
        
        if (data) {
          // Collect all owner IDs to fetch profiles
          const ownerIds = data.map(review => review.owner_user_id).filter(Boolean);
          
          // Fetch owner profiles
          await fetchOwnerProfiles(ownerIds);
          
          // Convert from DB format to Review format
          const formattedReviews: Review[] = data.map((review: ReviewFromDB) => ({
            id: review.id,
            title: review.title,
            description: review.description,
            keys: review.keys || 0,
            owner_user_id: review.owner_user_id,
            owner_name: getFormattedName(ownerProfiles[review.owner_user_id]),
            created_at: review.created_at,
            last_updated_at: review.last_updated_at,
            assignee: 'Unassigned', // Default assignee
            completed: review.status === 'completed', // Convert status to completed boolean for UI
            status: review.status
          }));
          
          setReviews(formattedReviews);
        }
      } catch (error) {
        setError('An unexpected error occurred. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchReviews();
  }, [user, projectId, ownerProfiles]);

  /**
   * Create a new review
   * @param title - The title of the review
   * @param description - The description of the review
   * @returns The created review or null if creation failed
   */
  const createReview = async (title: string = 'New Review', description: string = 'Review description') => {
    if (!user || !projectId) {
      return null;
    }
    
    try {
      setIsLoading(true);
      
      const now = new Date().toISOString();
      
      const newReview = {
        title,
        description,
        project_id: projectId,
        owner_user_id: user.id,
        keys: null, // This will be handled by the database if it has a default value
        created_at: now,
        last_updated_at: now,
        status: 'pending' // Default status is pending
      };
      
      const { data, error } = await supabase
        .from('Reviews')
        .insert([newReview])
        .select();
        
      if (error) {
        return null;
      }
      
      if (data && data.length > 0) {
        // Ensure we have the owner profile
        if (!ownerProfiles[user.id]) {
          await fetchOwnerProfiles([user.id]);
        }
        
        const formattedReview: Review = {
          id: data[0].id,
          title: data[0].title,
          description: data[0].description,
          keys: data[0].keys || 0,
          owner_user_id: data[0].owner_user_id,
          owner_name: getFormattedName(ownerProfiles[user.id]),
          created_at: data[0].created_at,
          last_updated_at: data[0].last_updated_at,
          assignee: 'Unassigned',
          completed: false, // New review is not completed
          status: data[0].status
        };
        
        // Add the new review and sort the array by last_updated_at
        setReviews(prevReviews => {
          const updatedReviews = [...prevReviews, formattedReview];
          return updatedReviews.sort((a, b) => {
            const dateA = a.last_updated_at ? new Date(a.last_updated_at).getTime() : 0;
            const dateB = b.last_updated_at ? new Date(b.last_updated_at).getTime() : 0;
            return dateB - dateA; // Sort in descending order (newest first)
          });
        });
        
        return formattedReview;
      }
      
      return null;
    } catch (error) {
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle the completion status of a review
   * @param reviewId - The ID of the review to toggle
   * @returns A boolean indicating whether the operation was successful
   */
  const toggleReviewCompletion = async (reviewId: string) => {
    const reviewToToggle = reviews.find(review => review.id === reviewId);
    
    if (!reviewToToggle || !projectId) {
      return false;
    }
    
    // Store the original reviews state for potential rollback
    const originalReviews = [...reviews];
    
    // Get the current time for updating
    const now = new Date().toISOString();
    
    // Optimistically update the UI
    setReviews(reviews.map(review => 
      review.id === reviewId 
        ? { 
            ...review, 
            completed: !review.completed, 
            status: review.completed ? 'pending' : 'completed',
            last_updated_at: now
          } 
        : review
    ).sort((a, b) => {
      const dateA = a.last_updated_at ? new Date(a.last_updated_at).getTime() : 0;
      const dateB = b.last_updated_at ? new Date(b.last_updated_at).getTime() : 0;
      return dateB - dateA; // Sort in descending order (newest first)
    }));
    
    try {
      // Determine the new status based on the current completed state
      const newStatus = reviewToToggle.completed ? 'pending' : 'completed';
      
      const { error } = await supabase
        .from('Reviews')
        .update({ 
          status: newStatus,
          last_updated_at: now
        })
        .eq('id', reviewId)
        .eq('project_id', projectId);
        
      if (error) {
        // Revert the optimistic update if there was an error
        setReviews(originalReviews);
        return false;
      }
      
      return true;
    } catch (error) {
      // Revert the optimistic update if there was an error
      setReviews(originalReviews);
      return false;
    }
  };

  /**
   * Get a review by ID
   * @param reviewId - The ID of the review to get
   * @returns The review or undefined if not found
   */
  const getReviewById = (reviewId: string) => {
    return reviews.find(review => review.id === reviewId);
  };

  /**
   * Fetch a review by ID directly from the database
   * @param reviewId - The ID of the review to fetch
   * @returns Promise resolving to the review or null if not found
   */
  const fetchReviewById = async (reviewId: string) => {
    if (!user || !reviewId) {
      return null;
    }
    
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('Reviews')
        .select('*')
        .eq('id', reviewId)
        .single();
        
      if (error) {
        return null;
      }
      
      if (data) {
        // Ensure we have the owner profile
        if (data.owner_user_id && !ownerProfiles[data.owner_user_id]) {
          await fetchOwnerProfiles([data.owner_user_id]);
        }
        
        const formattedReview: Review = {
          id: data.id,
          title: data.title,
          description: data.description,
          keys: data.keys || 0,
          owner_user_id: data.owner_user_id,
          owner_name: getFormattedName(ownerProfiles[data.owner_user_id]),
          created_at: data.created_at,
          last_updated_at: data.last_updated_at,
          assignee: 'Unassigned',
          completed: data.status === 'completed', // Convert status to completed boolean
          status: data.status
        };
        
        return formattedReview;
      }
      
      return null;
    } catch (error) {
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    reviews,
    isLoading,
    error,
    createReview,
    toggleReviewCompletion,
    getReviewById,
    fetchReviewById
  };
} 